import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { authService } from '../api/authService';
import { coordinatoreOreLockService } from '../api/services';
import { CoordinatoreOreLockStatus } from '../types/types';

const ALLOWED_PATHS = ['/orario-dipendente', '/orario-dipendenti-taste', '/generazione-orario', '/dashboard'];

// Percorsi sempre accessibili anche durante lock (logout, login, home, dashboard)
const EXEMPT_PATHS = ['/', '/login', '/logout', '/auth', '/dashboard'];

const buildOrarioDipendenteUrl = (lock: CoordinatoreOreLockStatus): string => {
  const target = lock.firstTarget;
  if (!target) return '/orario-dipendente';

  const params = new URLSearchParams();
  params.set('neg', target.negozio);
  params.set('dip', target.fullName || `${target.nome} ${target.cognome}`.trim());
  params.set('cp', target.codicePersonale);
  params.set('mese', String(new Date().getMonth() + 1));
  params.set('anno', String(new Date().getFullYear()));
  return `/orario-dipendente?${params.toString()}`;
};

const buildGenerazioneUrl = (neg: string): string => {
  const now = new Date();
  const params = new URLSearchParams();
  params.set('neg', neg);
  params.set('mese', String(now.getMonth() + 1));
  params.set('anno', String(now.getFullYear()));
  return `/generazione-orario?${params.toString()}`;
};

const CoordinatoreOreLockGuard: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const inFlight = useRef(false);
  const lastLockStateRef = useRef<boolean | null>(null);

  // Funzione per fare un check immediato del lock status
  const checkLockStatusNow = async () => {
    const user = authService.getCurrentUser();
    if (!user || user.ruolo !== 'Coordinatore') return;

    try {
      console.log('[Guard] checkLockStatusNow chiamato - fetching fresh lock status');
      const lock = await coordinatoreOreLockService.getStatus({ force: true });
      const isNowLocked = lock.locked;
      console.log('[Guard] Lock status:', isNowLocked, 'Target:', lock.firstTarget?.fullName);

      // Se stato del lock cambia da locked a unlocked, aggiorna solo lo stato interno.
      // Non navigare via dalla pagina corrente: su Orario Dipendente conferma/cancella
      // deve restare un refresh parziale della riga/calendario.
      if (lastLockStateRef.current === true && isNowLocked === false) {
        console.log('[Guard] Lock rilasciato');
        coordinatoreOreLockService.invalidateCache();
        lastLockStateRef.current = isNowLocked;
        return;
      }

      lastLockStateRef.current = isNowLocked;
    } catch (err) {
      console.error('[Guard] Errore check immediato lock:', err);
    }
  };

  useEffect(() => {
    const run = async () => {
      // Percorsi sempre accessibili: bypass guard
      if (EXEMPT_PATHS.includes(location.pathname)) {
        return;
      }

      const user = authService.getCurrentUser();
      if (!user || user.ruolo !== 'Coordinatore') {
        coordinatoreOreLockService.invalidateCache();
        return;
      }

      if (inFlight.current) return;
      inFlight.current = true;

      try {
        // Il redirect non può basarsi su un precedente locked=false in cache.
        const lock = await coordinatoreOreLockService.getStatus({ force: true });

        if (!lock.locked || !lock.firstTarget) {
          return;
        }

        if (!ALLOWED_PATHS.includes(location.pathname)) {
          navigate(buildOrarioDipendenteUrl(lock), { replace: true });
          return;
        }

        if (location.pathname === '/orario-dipendente' || location.pathname === '/orario-dipendenti-taste') {
          const params = new URLSearchParams(location.search);
          const currentCp = params.get('cp');
          // Permetti navigazione su qualunque dipendente con ore negative
          const allTargets = lock.allTargets ?? (lock.firstTarget ? [lock.firstTarget] : []);
          const isAllowedTarget = allTargets.some(t => t.codicePersonale === currentCp);
          if (!isAllowedTarget) {
            navigate(buildOrarioDipendenteUrl(lock), { replace: true });
          }
          return;
        }

        if (location.pathname === '/generazione-orario') {
          const params = new URLSearchParams(location.search);
          const currentNeg = params.get('neg');
          // Solo negozi DIRETTI con dipendenti negativi (da allTargets, non temporanei)
          const allTargets = lock.allTargets ?? (lock.firstTarget ? [lock.firstTarget] : []);
          const negoziConNegativi = Array.from(new Set(allTargets.map(t => t.negozio).filter(Boolean)));
          if (!currentNeg || !negoziConNegativi.includes(currentNeg)) {
            navigate(buildGenerazioneUrl(lock.firstTarget.negozio), { replace: true });
          }
        }
      } catch (err) {
        console.error('Errore controllo lock coordinatore ore:', err);
      } finally {
        inFlight.current = false;
      }
    };

    run();
  }, [location.pathname, location.search, navigate]);

  // Listener per evento di invalidazione cache (triggered da salvataggio turni)
  // Guard: solo Coordinatore riceve check
  useEffect(() => {
    const handleCacheInvalidated = (event: Event) => {
      const user = authService.getCurrentUser();
      if (!user || user.ruolo !== 'Coordinatore') return;
      const detail = (event as CustomEvent<{ url?: string }>).detail;
      if (location.pathname.startsWith('/orario-dipendente') && detail?.url?.includes('/orari-generati')) {
        console.log('[Guard] Write locale Orario Dipendente: skip check immediato lock');
        return;
      }
      console.log('[Guard] Cache invalidata - checking lock status immediately');
      checkLockStatusNow();
    };

    window.addEventListener('coordinatore-ore-lock-cache-invalidated', handleCacheInvalidated);
    console.log('[Guard] Listener registrato per coordinatore-ore-lock-cache-invalidated');
    
    return () => {
      window.removeEventListener('coordinatore-ore-lock-cache-invalidated', handleCacheInvalidated);
    };
  }, [location.pathname]);

  return null;
};

export default CoordinatoreOreLockGuard;
